import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { track } from '@/lib/analytics';
import { setOnboardingStepDone } from '@/lib/storage';
import { OnboardingProgress } from '@/components/OnboardingProgress';
import { Button } from '@/components';

const TOTAL_STEPS = 11;

const SITUATION_OPTIONS = [
  { id: 'just_separated', label: 'Just separated' },
  { id: 'divorced_1_3', label: 'Divorced 1-3 years' },
  { id: 'divorced_3_plus', label: 'Divorced 3+ years' },
  { id: 'never_married', label: 'Never married' },
];

function SituationPage() {
  const navigate = useNavigate();
  const [selectedSituation, setSelectedSituation] = useState<string | null>(
    null
  );

  useEffect(() => {
    track('Situation Screen');
  }, []);

  const handleContinue = () => {
    if (selectedSituation) {
      track('Situation Submitted', { situation: selectedSituation });
    }
    setOnboardingStepDone('situation');
    navigate({ to: '/app/onboarding/children-count' });
  };

  const handleSkip = () => {
    track('Situation Skipped');
    setOnboardingStepDone('situation');
    navigate({ to: '/app/onboarding/children-count' });
  };

  return (
    <div className='min-h-screen bg-sky-light flex flex-col'>
      <OnboardingProgress currentStep={4} totalSteps={TOTAL_STEPS} />
      <div className='flex-1 flex flex-col justify-between px-6 max-w-lg mx-auto w-full'>
        <div className='flex-1 pt-5'>
          <h1
            className='text-[28px] font-extrabold text-gray-900 text-center'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            What best describes your situation?
          </h1>
          <p
            className='mt-2.5 text-base text-gray-500 text-center leading-relaxed mb-6'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            This helps us understand your needs better.
          </p>

          <div className='space-y-2.5'>
            {SITUATION_OPTIONS.map((option) => {
              const isSelected = selectedSituation === option.id;
              return (
                <button
                  key={option.id}
                  onClick={() => setSelectedSituation(option.id)}
                  className={`w-full flex items-center gap-3.5 p-4 rounded-2xl border-2 transition-colors text-left ${
                    isSelected
                      ? 'border-primary bg-primary/[0.04]'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div
                    className={`w-[22px] h-[22px] rounded-full border-2 flex items-center justify-center ${
                      isSelected ? 'border-primary' : 'border-gray-200'
                    }`}
                  >
                    {isSelected && (
                      <div className='w-3 h-3 rounded-full bg-primary' />
                    )}
                  </div>
                  <span
                    className={`flex-1 text-base ${
                      isSelected
                        ? 'text-gray-900 font-semibold'
                        : 'text-gray-700'
                    }`}
                    style={{
                      fontFamily:
                        "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    }}
                  >
                    {option.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className='w-full pb-6 pt-4 space-y-2.5'>
          <Button
            fullWidth
            onClick={handleContinue}
            disabled={!selectedSituation}
          >
            Continue
          </Button>
          <button
            onClick={handleSkip}
            className='w-full py-3 text-center text-gray-500 font-semibold hover:text-gray-700 transition-colors'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/onboarding/situation')({
  component: SituationPage,
});
